import { flagApplicabilityFields, flagApplicabilityValue, hasFlagApplicability } from './flagApplicability';

export const SITE_ROLES = Object.freeze(['SeniorLocalUser', 'DutyManager', 'JuniorLocalUser']);
export const FLAG_ROLE_LABELS = Object.freeze({
  CustomerLevelUser: 'Customer Level',
  SeniorLocalUser: 'Senior Local User',
  DutyManager: 'Duty Manager',
  JuniorLocalUser: 'Junior Local User',
});
export const FLAG_DAYS = Object.freeze([
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]);
export const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;

export const flagAccount = (context) => {
  const roles = context && context.rawProfile && context.rawProfile.roles;
  if (!Array.isArray(roles) || roles.length !== 1) {
    return { error: 'A single applicable authenticated role is required. Sign in with an account that has exactly one role.' };
  }
  const role = roles[0];
  if (role !== 'CustomerLevelUser' && !SITE_ROLES.includes(role)) {
    return { error: 'This account cannot use the customer or site Flag System APIs.' };
  }
  if (SITE_ROLES.includes(role)) {
    return { role, isCustomer: false, error: 'BACKEND NOT YET AVAILABLE: the current-user API does not expose your assigned customer and locations. No member or scan data can be loaded safely.' };
  }
  const customerId = context.claims && context.claims.customerId;
  if (!positiveId(customerId)) return { role, error: 'Your authenticated profile does not contain a valid customer. Please contact support.' };
  return { role, isCustomer: true, customerId: Number(customerId) };
};
export const canEnterFlagSystem = (context) => Boolean(context &&
  (context.role === 'CustomerLevelUser' || SITE_ROLES.includes(context.role)));

export const emptyFlag = () => ({
  name: '', description: '', juniorAction: '', dutyManagerAction: '', seniorAction: '',
  blockAllTime: true, active: true, revision: 0, times: [],
  applyOnEntry: true, applyOnExit: true,
  permissions: SITE_ROLES.map(role => ({ role, canOverride: false, canAddRemove: false })),
});
export const flagRequest = (flag, includeApplicability = hasFlagApplicability(flag)) => ({
  name: flag.name.trim(), description: flag.description || null,
  juniorAction: flag.juniorAction || null, dutyManagerAction: flag.dutyManagerAction || null,
  seniorAction: flag.seniorAction || null,
  blockAllTime: flag.blockAllTime, active: flag.active, revision: flag.revision,
  times: flag.blockAllTime ? [] : flag.times.map(time => ({ ...time })),
  permissions: flag.permissions.map(permission => ({ ...permission })),
  ...(includeApplicability ? flagApplicabilityFields(flagApplicabilityValue(flag)) : {}),
});
const timeMinutes = (time, end) => {
  if (end && time === '24:00') return 1440;
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time || '')) return null;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};
export const validateFlag = (flag, { validateApplicability = true } = {}) => {
  const errors = {};
  if (validateApplicability && !flagApplicabilityValue(flag)) errors.applicability = 'Choose at least one direction: Entry only, Exit only, or Entry and exit.';
  if (!flag.name.trim()) errors.name = 'Flag Name is required.';
  else if (flag.name.length > 200) errors.name = 'Use at most 200 characters.';
  ['description', 'juniorAction', 'dutyManagerAction', 'seniorAction'].forEach(field => {
    if (flag[field] && flag[field].length > 2000) errors[field] = 'Use at most 2000 characters.';
  });
  if (!Array.isArray(flag.times) || flag.times.some(time => !time)) errors.times = 'Time allowances must be a list without empty entries.';
  else if (flag.blockAllTime && flag.times.length) errors.times = 'Block all time must not include time allowances.';
  else if (!flag.blockAllTime) {
    if (!flag.times.length) errors.times = 'Select at least one day and a valid time allowance.';
    const days = new Set();
    flag.times.forEach((time, index) => {
      const start = timeMinutes(time.start, false);
      const end = timeMinutes(time.end, true);
      if (!Number.isInteger(time.dayOfWeek) || time.dayOfWeek < 0 || time.dayOfWeek > 6 || days.has(time.dayOfWeek)) {
        errors.times = 'Configure each valid weekday only once.';
      }
      days.add(time.dayOfWeek);
      if (start === null || end === null || start >= end) {
        errors[`time${index}`] = 'Use HH:mm with start before end. End may be 24:00; overnight ranges are not supported.';
      }
    });
  }
  if (!Array.isArray(flag.permissions) || flag.permissions.length !== 3 ||
      SITE_ROLES.some(role => flag.permissions.filter(permission => permission && permission.role === role &&
        typeof permission.canOverride === 'boolean' && typeof permission.canAddRemove === 'boolean').length !== 1)) {
    errors.permissions = 'Configure one independent permission entry for each of the three site roles.';
  }
  return errors;
};
export const validateMemberChange = (memberId, comment, isCustomer) => {
  const errors = {};
  if (!memberId.trim() || memberId.trim().length > 200) errors.memberId = 'Enter a Member ID of 1 to 200 characters.';
  if (!isCustomer && !comment.trim()) errors.comment = 'A comment is required for site users.';
  if (comment.length > 1000) errors.comment = 'Use at most 1000 characters.';
  return errors;
};
export const memberFlagView = (flag, isCustomer) => isCustomer ? {
  flagId: flag.flagId, name: flag.name, description: flag.description,
  action: null, canAddRemove: true, canOverride: true,
} : {
  flagId: flag.flagId, name: flag.name,
  description: flag.canAddRemove ? flag.description : null,
  action: flag.action, canAddRemove: flag.canAddRemove === true, canOverride: flag.canOverride === true,
};
export const validateSiteUser = (user, locations) => {
  const errors = {};
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email.trim())) errors.email = 'Enter a valid email address.';
  if (!SITE_ROLES.includes(user.role)) errors.role = 'Select one of the three site roles.';
  if (!user.locationIds.length || user.locationIds.some(id => !positiveId(id) || !locations.some(location => location.id === id))) {
    errors.locationIds = 'Select at least one location belonging to your customer.';
  }
  return errors;
};
export const siteUserRequest = (user) => ({
  email: user.email.trim(), name: user.name || '', surname: user.surname || '',
  role: user.role, locationIds: Array.from(new Set(user.locationIds)),
});
export const flagAccessSummary = (flag) => flag.blockAllTime ? 'Block all time' :
  flag.times.map(time => `${FLAG_DAYS.find(day => day.value === time.dayOfWeek).label} ${time.start} - ${time.end}`).join(', ');

export const formatFlagInstant = (value) => {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Invalid server timestamp' : new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short',
  }).format(date);
};
export const formatLegacyScan = (value) => {
  if (!value) return 'Not provided';
  // Existing SupportLogs treats offset-free legacy values as UTC.
  const instant = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  return formatFlagInstant(instant);
};

export const flagError = (error, fallback) => {
  const status = error && error.response && error.response.status;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'This account is not authorized for the requested customer, location or action.';
  if (status === 404) return 'The requested item is unavailable in this scope.';
  if (status >= 500) return 'The server could not complete this request. Please try again later.';
  if (!error.response) return `${fallback} Reload the current data before repeating a change whose outcome is uncertain.`;
  const data = error.response.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (Array.isArray(data)) return data.map(item => item.description).filter(Boolean).join(' ') || fallback;
  if (data && typeof data === 'object') {
    if (data.errors) return Object.values(data.errors).reduce((all, item) => all.concat(item), []).join(' ') || fallback;
    if (data.error) return data.error;
    if (data.detail || data.title) return data.detail || data.title;
    const messages = Object.values(data).filter(value => Array.isArray(value));
    if (messages.length) return [].concat(...messages).join(' ');
  }
  return status === 409 ? 'The data changed or this action conflicts with the current state. Reload before trying again.' : fallback;
};
export const flagFieldErrors = (error) => {
  const data = error && error.response && error.response.data;
  const errors = data && data.errors;
  if (!errors || typeof errors !== 'object') return {};
  return Object.keys(errors).reduce((fields, key) => {
    const name = key.replace(/^\$\./, '');
    const field = name.charAt(0).toLowerCase() + name.slice(1);
    fields[field] = [].concat(errors[key]).join(' ');
    return fields;
  }, {});
};
